import React, { useMemo, useState } from 'react';
import { Clock, Plus, Trash2 } from 'lucide-react';
import ExerciseCard from './ExerciseCard';
import { estimateMinutes } from '../WorkoutPlayer/playerLogic';

// Um dia do plano, em coluna: nome editável no sítio, quanto tempo leva, a lista de exercícios em cartões e a zona
// onde se largam os exercícios da biblioteca. Em ecrã estreito diz como se acrescenta sem arrastar.

/** Os campos do rascunho → a forma que o motor de estimativa do player já usa (uma conta só, para não divergir). */
const asPlayerShape = (ex) => ({
  trackingType: ex.trackingType || 'weight-reps',
  prescription: {
    setsTarget: Number(ex.setsTarget) || null,
    repsMin: Number(ex.repsMin) || null,
    repsMax: Number(ex.repsMax) || null,
    restSeconds: Number(ex.restSeconds) || null,
  },
});

export default function DayColumn({
  day, dayIndex, narrow = false, editing = true, selectedPath,
  onRenameDay, onSelectExercise, onFieldChange, onAddExercise, onRemoveExercise, onRemoveDay,
  onDropDay, onDragStartExercise, onReplace, onPreview,
}) {
  const [over, setOver] = useState(false);
  const exercises = useMemo(() => day.exercises || [], [day.exercises]);
  const minutes = useMemo(() => (exercises.length === 0 ? 0 : estimateMinutes(exercises.map(asPlayerShape))), [exercises]);
  const sets = useMemo(() => exercises.reduce((n, e) => n + (Number(e.setsTarget) || 0), 0), [exercises]);

  return (
    <div
      className={`ps-day ${over ? 'is-over' : ''}`}
      data-testid={`plan-day-${dayIndex}`}
      onDragOver={editing ? (e) => { e.preventDefault(); setOver(true); } : undefined}
      onDragLeave={editing ? () => setOver(false) : undefined}
      onDrop={editing ? (e) => { setOver(false); onDropDay?.(e, dayIndex); } : undefined}
    >
      <div className="ps-day-head">
        {editing ? (
          <div className="ps-day-name">
            <input value={day.dayName || ''} placeholder={`Dia ${dayIndex + 1}`} aria-label={`Nome do dia ${dayIndex + 1}`}
              onChange={(e) => onRenameDay(dayIndex, e.target.value)} data-testid={`plan-day-name-${dayIndex}`} />
          </div>
        ) : (
          <div className="ps-day-name">{day.dayName || `Dia ${dayIndex + 1}`}</div>
        )}
        <div className="ps-day-actions">
          {editing ? (
            <button type="button" className="ps-chip" onClick={() => onAddExercise(dayIndex)} data-testid={`plan-add-ex-${dayIndex}`} title="Acrescentar exercício">➕ Exercício</button>
          ) : null}
          {editing && (day.exercises || []).length > 0 ? (
            <button type="button" className="ps-chip" onClick={() => onRemoveDay(dayIndex)} title="Remover dia" aria-label={`Remover dia ${dayIndex + 1}`} data-testid={`plan-remove-day-${dayIndex}`}>
              <Trash2 size={13} aria-hidden="true" />
            </button>
          ) : null}
        </div>
      </div>

      <div className="ps-day-meta">
        <span>{exercises.length === 1 ? '1 exercício' : `${exercises.length} exercícios`}</span>
        {sets > 0 ? <span>· {sets} séries</span> : null}
        {minutes > 0 ? <span><Clock size={13} aria-hidden="true" style={{ verticalAlign: '-2px' }} /> ~{minutes} min</span> : null}
      </div>

      {editing ? (
        <div className={`ps-drop ${narrow ? 'is-small' : ''}`} data-testid={`plan-drop-${dayIndex}`}>
          {narrow ? 'Para acrescentar da biblioteca: escolha o dia e toque em ➕ na biblioteca, mais abaixo' : 'Arraste exercícios da biblioteca para aqui'}
        </div>
      ) : null}

      {exercises.length === 0 ? <div className="ps-empty">Sem exercícios.</div> : null}

      {exercises.map((ex, exIndex) => (
        <ExerciseCard
          key={`${dayIndex}-${exIndex}`}
          exercise={ex}
          index={exIndex}
          editing={editing}
          selected={selectedPath?.dayIndex === dayIndex && selectedPath?.exIndex === exIndex}
          onSelect={() => onSelectExercise(dayIndex, exIndex)}
          onFieldChange={(field, value) => onFieldChange(dayIndex, exIndex, field, value)}
          onReplace={onReplace ? () => onReplace(ex) : undefined}
          onRemove={() => onRemoveExercise(dayIndex, exIndex)}
          onMove={onDragStartExercise ? (e) => onDragStartExercise(e, dayIndex, exIndex) : undefined}
          onPreview={onPreview ? (item) => onPreview(item) : undefined}
        />
      ))}

      {editing ? (
        <button type="button" className="ps-chip" onClick={() => onAddExercise(dayIndex)} data-testid={`plan-add-ex-bottom-${dayIndex}`}>
          <Plus size={13} aria-hidden="true" /> Acrescentar exercício
        </button>
      ) : null}
    </div>
  );
}
